import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ArrowUpDown, Briefcase, Building2, Plus, Search, Users2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import type { Club, Provider } from "@/types";
import { useAuthStore } from "@/store/auth";
import { Button } from "@/components/ui/Button";
import { Card, Badge } from "@/components/ui/Card";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { TableSkeleton } from "@/components/ui/Skeleton";
import { Breadcrumbs } from "@/components/ui/Breadcrumbs";
import { Pagination } from "@/components/ui/Pagination";
import { ContactButtons } from "@/components/ContactButtons";

// Misma forma que backend/src/utils/pagination.ts#PaginatedResponse — no hay tipos compartidos
// entre frontend y backend en este proyecto, se replica igual que ya hace types.ts con el resto
// de formas de respuesta.
interface PaginatedResponse<T> {
  data: T[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

type SortKey = "nombre" | "usuarios" | "activo";

function SortHeader({
  label,
  active,
  dir,
  onClick,
}: {
  label: string;
  active: boolean;
  dir: "asc" | "desc";
  onClick: () => void;
}) {
  const Icon = !active ? ArrowUpDown : dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1 font-semibold transition-colors hover:text-ink-700 ${
        active ? "text-ink-700" : ""
      }`}
    >
      {label}
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}

export function ClubsPage() {
  const isSuperAdmin = useAuthStore((s) => s.user?.role === "SUPER_ADMIN");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirmClub, setConfirmClub] = useState<Club | null>(null);
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" }>({ key: "nombre", dir: "asc" });

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [activo, setActivo] = useState<"" | "true" | "false">("");
  const [providerId, setProviderId] = useState("");

  // Pequeño debounce manual del buscador (no hay hook compartido para esto en el proyecto): el
  // input se actualiza al tecleo, pero solo se manda a la API 400ms después de dejar de escribir.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data: providers } = useQuery({
    queryKey: ["providers"],
    queryFn: async () => (await api.get<{ data: Provider[] }>("/providers?limit=100")).data.data,
    enabled: isSuperAdmin,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["clubs", { page, limit, search, activo, providerId: isSuperAdmin ? providerId : "" }],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (search) params.set("nombre", search);
      if (activo) params.set("activo", activo);
      if (isSuperAdmin && providerId) params.set("providerId", providerId);
      return (await api.get<PaginatedResponse<Club>>(`/clubs?${params.toString()}`)).data;
    },
    placeholderData: keepPreviousData,
  });

  const toggleActivo = useMutation({
    mutationFn: ({ id, activo }: { id: number; activo: boolean }) => api.put(`/clubs/${id}`, { activo }),
    onSuccess: (_res, vars) => {
      queryClient.invalidateQueries({ queryKey: ["clubs"] });
      toast.success(vars.activo ? "Club activado" : "Club desactivado");
    },
    onError: () => toast.error("No se pudo actualizar el club"),
  });

  function toggleSort(key: SortKey) {
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  }

  const sortedClubs = useMemo(() => {
    const clubs = data?.data ?? [];
    const dirMult = sort.dir === "asc" ? 1 : -1;
    return [...clubs].sort((a, b) => {
      if (sort.key === "nombre") return a.nombre.localeCompare(b.nombre) * dirMult;
      if (sort.key === "usuarios") return ((a._count?.usuarios ?? 0) - (b._count?.usuarios ?? 0)) * dirMult;
      return (Number(a.activo) - Number(b.activo)) * dirMult;
    });
  }, [data, sort]);

  const hayFiltrosActivos = Boolean(search || activo || providerId);

  return (
    <div className="flex flex-col gap-8">
      <Breadcrumbs items={[{ label: "Clubes" }]} />

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
            <Building2 className="h-6 w-6" />
          </span>
          <div>
            <h1 className="text-2xl font-bold text-ink-900">Clubes</h1>
            <p className="text-ink-400">Alta y gestión de los clubes de la plataforma. Pincha en uno para ver su ficha.</p>
          </div>
        </div>
        <Button onClick={() => navigate("/clubes/nuevo")}>
          <Plus className="h-4 w-4" />
          Nuevo club
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="relative max-w-xs flex-1 basis-56">
          <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-ink-300" />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Buscar club…"
            className="w-full rounded-xl border border-ink-100 bg-white py-2 pl-9 pr-3 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
          />
        </div>

        {isSuperAdmin && (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="club-provider-filter" className="text-xs font-medium text-ink-400">
              Proveedor
            </label>
            <select
              id="club-provider-filter"
              value={providerId}
              onChange={(e) => {
                setProviderId(e.target.value);
                setPage(1);
              }}
              className="rounded-xl border border-ink-100 bg-white px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
            >
              <option value="">Todos los proveedores</option>
              {providers?.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="flex flex-col gap-1.5">
          <label htmlFor="club-activo-filter" className="text-xs font-medium text-ink-400">
            Estado
          </label>
          <select
            id="club-activo-filter"
            value={activo}
            onChange={(e) => {
              setActivo(e.target.value as "" | "true" | "false");
              setPage(1);
            }}
            className="rounded-xl border border-ink-100 bg-white px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
          >
            <option value="">Todos los estados</option>
            <option value="true">Activos</option>
            <option value="false">Inactivos</option>
          </select>
        </div>
      </div>

      <Card className="overflow-hidden p-0">
        {isLoading ? (
          <TableSkeleton rows={3} cols={4} />
        ) : !sortedClubs.length ? (
          <p className="p-6 text-sm text-ink-400">
            {hayFiltrosActivos ? "Ningún club coincide con los filtros." : "Todavía no hay clubes creados."}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead className="bg-ink-50/60 text-xs tracking-wide text-ink-400 uppercase">
                <tr>
                  <th className="px-6 py-3">
                    <SortHeader label="Nombre" active={sort.key === "nombre"} dir={sort.dir} onClick={() => toggleSort("nombre")} />
                  </th>
                  <th className="px-6 py-3">
                    <SortHeader label="Usuarios" active={sort.key === "usuarios"} dir={sort.dir} onClick={() => toggleSort("usuarios")} />
                  </th>
                  {isSuperAdmin && <th className="px-6 py-3 font-semibold">Proveedor</th>}
                  <th className="px-6 py-3">
                    <SortHeader label="Estado" active={sort.key === "activo"} dir={sort.dir} onClick={() => toggleSort("activo")} />
                  </th>
                  <th className="px-6 py-3 font-semibold"></th>
                </tr>
              </thead>
              <tbody>
                {sortedClubs.map((club) => (
                  <tr
                    key={club.id}
                    onClick={() => navigate(`/clubes/${club.id}`)}
                    className="cursor-pointer border-t border-ink-100 transition-colors hover:bg-ink-50/50"
                  >
                    <td className="px-6 py-3.5 font-medium text-ink-800">
                      <span className="flex items-center gap-2">
                        {club.nombre}
                        <ContactButtons telefono={club.telefono} email={club.email} />
                      </span>
                    </td>
                    <td className="px-6 py-3.5 text-ink-400">
                      <span className="inline-flex items-center gap-1.5">
                        <Users2 className="h-3.5 w-3.5" />
                        {club._count?.usuarios ?? 0}
                      </span>
                    </td>
                    {isSuperAdmin && (
                      <td className="px-6 py-3.5">
                        {club.provider ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/proveedores/${club.provider!.id}`);
                            }}
                            className="inline-flex items-center gap-1.5 text-ink-500 transition-colors hover:text-brand-600 hover:underline"
                          >
                            <Briefcase className="h-3.5 w-3.5" />
                            {club.provider.nombre}
                          </button>
                        ) : (
                          <span className="text-ink-300">—</span>
                        )}
                      </td>
                    )}
                    <td className="px-6 py-3.5">
                      <Badge tone={club.activo ? "green" : "red"}>{club.activo ? "Activo" : "Inactivo"}</Badge>
                    </td>
                    <td className="px-6 py-3.5 text-right">
                      <Button
                        className="w-24"
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmClub(club);
                        }}
                      >
                        {club.activo ? "Desactivar" : "Activar"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {data && (
        <Pagination
          page={page}
          totalPages={data.totalPages}
          total={data.total}
          onPageChange={setPage}
          limit={limit}
          onLimitChange={(l) => {
            setLimit(l);
            setPage(1);
          }}
        />
      )}

      {confirmClub && (
        <ConfirmDialog
          open={!!confirmClub}
          onOpenChange={(open) => !open && setConfirmClub(null)}
          title={confirmClub.activo ? "Desactivar club" : "Activar club"}
          description={`¿Seguro que quieres ${confirmClub.activo ? "desactivar" : "activar"} "${confirmClub.nombre}"?`}
          tone="primary"
          confirmLabel={confirmClub.activo ? "Desactivar" : "Activar"}
          onConfirm={() => toggleActivo.mutate({ id: confirmClub.id, activo: !confirmClub.activo })}
        />
      )}
    </div>
  );
}
